import {
  DraftComplete,
  GuessModes,
  Settings,
  TeamColors,
  TeamColorsValues,
  TeamComplete,
} from '../common.type';
import styled from 'styled-components';
import { Brawler } from './Brawler';
import { useContext, useMemo } from 'react';
import { LayoutContext } from '../context/LayoutContext';
import { Coin } from './Coin';
import { ensureTeamIdx } from '../helpers/ensureTeamIdx';
import { Eye } from './Eye';
import { Share } from './Share';
import { LogsStatus } from '../type';
import { FormattedMessage, useIntl } from 'react-intl';
import { Status } from './Status';
import { TextCSS } from './Text';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.grey};
  overflow: hidden;
  border-radius: 1vmin;
  border: 0.4vmin solid #000;
  box-shadow: 0 0.5vmin rgba(0, 0, 0, 0.3);
`;
const WrapperTeam = styled.div`
  display: flex;
  flex-direction: row;
`;
const TeamDiv = styled.div<{ $color: TeamColorsValues }>`
  display: flex;
  flex-direction: row;
  padding: 2vmin;
  gap: 1vmin;
  background: ${(props) => props.theme.team[props.$color]};
`;
const StatusDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex: 1;
  justify-content: center;
`;
const FooterDiv = styled.div`
  display: flex;
  flex-direction: row;
  padding: 1vmin;
  align-items: center;
`;

const HeaderDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2vmin;
  padding: 1vmin;
`;
const MapMode = styled.img`
  width: ${(props) => `4${props.theme.unit}`};
  height: ${(props) => `4${props.theme.unit}`};
`;
const MapName = styled.div`
  ${TextCSS({})}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Team = ({
  settings,
  team,
  color,
}: {
  team: TeamComplete;
  color: TeamColorsValues;
  settings: Settings;
}) => {
  const { displayMode, brawlers } = useContext(LayoutContext);
  return (
    <TeamDiv $color={color}>
      {Object.values(team).map((v, i) => {
        const idx = i + 1;
        if (!ensureTeamIdx(idx)) return;
        const brawler = brawlers.find((b) => b.id === v.id);
        if (!brawler) return;
        return (
          <Brawler key={v.id} brawler={brawler} mode="list">
            {settings.coin === color && idx === 1 && (
              <Coin color={color} $displayMode={displayMode} />
            )}
            {((settings.mode === GuessModes.LAST &&
              settings.coin !== color &&
              idx === 3) ||
              (settings.mode === GuessModes.TWO_LAST && idx === 3)) && (
              <Eye $displayMode={displayMode} />
            )}
          </Brawler>
        );
      })}
    </TeamDiv>
  );
};

export const ListTeams = ({
  draft,
  token,
  status,
}: {
  draft: DraftComplete;
  token: string;
  status?: LogsStatus;
}) => {
  const intl = useIntl();
  const { maps } = useContext(LayoutContext);
  const map = useMemo(() => {
    return maps.find((m) => m.id === draft.mapId);
  }, [draft.mapId, maps]);

  return (
    <Wrapper>
      {map && (
        <HeaderDiv>
          <MapMode
            src={`${process.env.PUBLIC_URL}/images/game-modes/${map.mode}.webp`}
            alt=""
          />
          <MapName>{map.name}</MapName>
        </HeaderDiv>
      )}
      <WrapperTeam>
        <Team
          settings={draft.settings}
          team={draft.blueTeam}
          color={TeamColors.BLUE}
        />
        <Team
          settings={draft.settings}
          team={draft.redTeam}
          color={TeamColors.RED}
        />
      </WrapperTeam>
      <FooterDiv>
        <StatusDiv>
          {status && (
            <Status $color={status.win ? 'green' : 'red'} $size="M">
              <FormattedMessage id={status.win ? 'victory' : 'defeat'} />
            </Status>
          )}
        </StatusDiv>
        <Share
          data={{
            text: intl.formatMessage({ id: 'listTeamShareText' }),
            url: process.env.REACT_APP_URL + '/draft/guess/' + btoa(token),
          }}
        />
      </FooterDiv>
    </Wrapper>
  );
};
